import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, MapPinIcon, ZapIcon, TrendingUpIcon, BarChart3Icon, UsersIcon, MessageSquareIcon, PhoneIcon, GlobeIcon, CheckCircleIcon, MousePointerClickIcon } from 'lucide-react';
import { ScrollReveal } from '../../components/ScrollReveal';
import { SEOHead } from '../../components/SEOHead';
const faqs = [{
  question: 'How did Viridia help Finish Coat Painting increase revenue?',
  answer: 'Using the Never Miss a Lead package, Viridia implemented AI-powered messaging to handle inbound Yelp conversations instantly. In 30 days, the AI booked 29 estimates, generating $15.05k in closed-won revenue from end-to-end AI bookings alone - not including revenue from phone calls or website leads.'
}, {
  question: 'Can AI follow-up actually close jobs for contractors?',
  answer: 'Yes. The Finish Coat Painting case study proves that AI follow-up does more than just respond fast - it helps close real jobs. By engaging high-intent local demand immediately, AI turns inquiries into booked estimates and closed revenue.'
}];
export function FinishCoatPaintingPage() {
  return <div className="bg-viridia-black min-h-screen pt-24" data-id="element-1286" data-mp="lxnwipuxrh2e">
      <SEOHead title="Finish Coat Painting Case Study | $15.05k AI Revenue | Viridia Analytics" description="See how Finish Coat Painting used Viridia Analytics' Never Miss a Lead package to get 29 AI-booked estimates and $15.05k in closed-won revenue in 30 days." canonical="/case-studies/finish-coat-painting" keywords="painting marketing case study, Yelp ads for painters, AI lead conversion, Finish Coat Painting" faq={faqs} data-id="element-1287" data-mp="lz8pwc5iktyo" />

      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1288" data-mp="zz0boz29ven8">
        <ScrollReveal data-id="element-1289" data-mp="36nn1qrkljc1b">
          <div className="mb-6" data-id="element-1290" data-mp="36oy4qp67wfrd">
            <Link to="/case-studies" className="inline-flex items-center text-gray-400 hover:text-white transition-colors text-sm font-medium" data-id="element-1291" data-mp="36q9a2tfb9vkl">
              <ArrowRightIcon className="w-4 h-4 mr-2 rotate-180" data-id="element-1292" data-mp="15nrw6u58sa8i" />
              Back to Case Studies
            </Link>
          </div>

          <div className="bg-viridia-surface border border-gray-800 rounded-2xl overflow-hidden shadow-2xl" data-id="element-1293" data-mp="1fkd17fh9hlcc">
            {/* Header / Snapshot */}
            <div className="bg-gray-900 p-8 lg:p-12 relative overflow-hidden border-b border-gray-800" data-id="element-1294" data-mp="1jn9pnmthgbo7">
              <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-[100px]" data-id="element-1295" data-mp="1ok9trs6rv4co"></div>
              <div className="relative z-10" data-id="element-1296" data-mp="1tht96vf7hn67">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-orange-500/10 text-orange-400 text-xs font-bold tracking-wide uppercase mb-6 border border-orange-500/20" data-id="element-1297" data-mp="1ya2xy84l4h6w">
                  Painting Success Story
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight max-w-4xl" data-id="element-1298" data-mp="2797td5kunueu">
                  Finish Coat Painting: 29 AI-Booked Estimates and $15.05k in
                  Closed Revenue in 30 Days
                </h1>

                {/* Snapshot Pills */}
                <div className="flex flex-wrap gap-3 mb-10" data-id="element-1299" data-mp="3rs0g6fvc70yh">
                  <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-viridia-black border border-gray-700 text-gray-300 text-sm" data-id="element-1300" data-mp="3nqyfrvpooiux">
                    <MapPinIcon className="w-4 h-4 mr-2 text-orange-500" data-id="element-1301" data-mp="3iuq5avmfldu3" />
                    Residential/Commercial Painting
                  </div>
                  <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-viridia-black border border-gray-700 text-gray-300 text-sm" data-id="element-1302" data-mp="fbog0u99npqb">
                    <ZapIcon className="w-4 h-4 mr-2 text-orange-500" data-id="element-1303" data-mp="aj3pq2pkjurj" />
                    Never Miss a Lead Package
                  </div>
                </div>

                {/* Hero Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6" data-id="element-1304" data-mp="133lu6s06utul">
                  <div className="bg-viridia-black/50 p-6 rounded-xl border border-gray-800" data-id="element-1305" data-mp="y97vogq8c6lp">
                    <p className="text-3xl sm:text-4xl font-bold text-orange-500 mb-2" data-id="element-1306" data-mp="htq9wucoc4rt">
                      29
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-1307" data-mp="8ulbdo144bfn">
                      AI-Booked Estimates
                    </p>
                  </div>
                  <div className="bg-viridia-black/50 p-6 rounded-xl border border-gray-800" data-id="element-1308" data-mp="duhivx90qkzy">
                    <p className="text-3xl sm:text-4xl font-bold text-orange-500 mb-2" data-id="element-1309" data-mp="11kk3jsalsooj">
                      $15.05k
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-1310" data-mp="15mow0npbff9l">
                      Closed-Won Revenue
                    </p>
                  </div>
                  <div className="bg-viridia-black/50 p-6 rounded-xl border border-gray-800" data-id="element-1311" data-mp="wr978l3f3qav">
                    <p className="text-3xl sm:text-4xl font-bold text-orange-500 mb-2" data-id="element-1312" data-mp="1kh3gksoj2wqd">
                      138
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-1313" data-mp="1paxwrm4xctkp">
                      Total Leads
                    </p>
                  </div>
                  <div className="bg-viridia-black/50 p-6 rounded-xl border border-gray-800" data-id="element-1314" data-mp="1umv07lvgdlla">
                    <p className="text-3xl sm:text-4xl font-bold text-orange-500 mb-2" data-id="element-1315" data-mp="28dq1abzpmyeb">
                      64%
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-1316" data-mp="23f5bz7lzxz4n">
                      Lead Growth
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Challenge / Solution */}
            <div className="p-8 lg:p-12 bg-viridia-black border-b border-gray-800" data-id="element-1317" data-mp="1pni388avay4z">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12" data-id="element-1318" data-mp="1knvqs12v5lx8">
                <div data-id="element-1319" data-mp="1kpyn12um0bae">
                  <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-6" data-id="element-1320" data-mp="311vmezuoepeo">
                    <TrendingUpIcon className="w-6 h-6 text-red-500" data-id="element-1321" data-mp="2w3kcyq8amgwz" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-1322" data-mp="3440drv22nzw5">
                    The Challenge
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-1323" data-mp="384amvgl5v6f8">
                    Finish Coat Painting was generating Yelp demand but couldn't
                    keep up with inbound conversations. Leads were slipping
                    through the cracks because the team was busy on job sites,
                    and follow-up was inconsistent.
                  </p>
                </div>
                <div data-id="element-1324" data-mp="2pfvvgs6xlqdw">
                  <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-6" data-id="element-1325" data-mp="2pfdop1mn7trq">
                    <BarChart3Icon className="w-6 h-6 text-orange-500" data-id="element-1326" data-mp="2ucm793bpdzti" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-1327" data-mp="2z5ymwnyemwa4">
                    The Viridia Solution
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-1328" data-mp="26o32gxrv4566">
                    We deployed the Never Miss a Lead package - Yelp Ads
                    management with GEO targeting, AI-powered messaging to
                    handle inbound conversations instantly, and automated
                    estimate booking so every inquiry gets a response in
                    seconds.
                  </p>
                </div>
              </div>
            </div>

            {/* AI Bookings & Revenue */}
            <div className="grid grid-cols-1 md:grid-cols-2 border-b border-gray-800" data-id="element-1329" data-mp="1g6hhzd8vwmiw">
              <div className="p-8 lg:p-12 bg-viridia-surface border-r border-gray-800" data-id="element-1330" data-mp="1bfj04988zgdj">
                <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-6" data-id="element-1331" data-mp="16g61ysbxemvn">
                  <ZapIcon className="w-6 h-6 text-orange-500" data-id="element-1332" data-mp="11hbkputlx6dn" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-1333" data-mp="11f8o2rxy8ezy">
                  29 AI-Booked Estimates
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6" data-id="element-1334" data-mp="sg51hdzkphgp">
                  In 30 days, the AI booked 29 estimates (roughly one per day).
                  One booking actually represented 2 separate guys getting
                  quotes. These were{' '}
                  <strong className="text-white" data-id="element-1335" data-mp="2l9tjy38xgs2m">AI-worked leads</strong>, not
                  random CRM leads. The AI handled the conversation, qualified
                  the prospect, and booked the estimate.
                </p>
              </div>
              <div className="p-8 lg:p-12 bg-viridia-surface" data-id="element-1336" data-mp="1xlk19a70gkfp">
                <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-6" data-id="element-1337" data-mp="22d1ufc2slgq4">
                  <UsersIcon className="w-6 h-6 text-orange-500" data-id="element-1338" data-mp="1noxhypuc21fs" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-1339" data-mp="1sn8sz3wd6hzl">
                  $15.05k Closed-Won Revenue
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6" data-id="element-1340" data-mp="1ep0pse8qikfz">
                  The 29 AI-booked estimates resulted in $15.05k in closed-won
                  revenue. This revenue came{' '}
                  <strong className="text-white" data-id="element-1341" data-mp="10223sysnci8n">
                    from end-to-end AI bookings alone
                  </strong>{' '}
                  - it does NOT include revenue from phone calls or website
                  leads. This proves that AI follow-up doesn't just respond fast
                  - it helps close real jobs.
                </p>
              </div>
            </div>

            {/* 30-Day Performance Breakdown */}
            <div className="p-8 lg:p-12 bg-viridia-black border-b border-gray-800" data-id="element-1342" data-mp="1ixjq6md2nv7n">
              <h3 className="text-2xl font-bold text-white mb-8" data-id="element-1343" data-mp="1e0jmuj7lx6k8">
                30-Day Performance
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-1344" data-mp="1nql8z5gegewi">
                <div className="space-y-6" data-id="element-1345" data-mp="1nreige6qtiic">
                  <div className="bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-1346" data-mp="21q4dzpdwza4l">
                    <div className="flex justify-between items-center mb-2" data-id="element-1347" data-mp="1wrjgvytyl6kd">
                      <span className="text-gray-400" data-id="element-1348" data-mp="1wpge0e7pjiy8">Impressions</span>
                      <span className="text-white font-bold" data-id="element-1349" data-mp="y4nfn0tkoxc9">33.2k</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2 mb-2" data-id="element-1350" data-mp="tat1se0nfcl3">
                      <div className="bg-orange-500 h-2 rounded-full" style={{
                      width: '89%'
                    }} data-id="element-1351" data-mp="pbtxmvmqrjup"></div>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-1352" data-mp="1gzlyicp3hogr">
                      29.4k (89%) from Yelp Ads
                    </p>
                  </div>
                  <div className="bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-1353" data-mp="18076v9x6z7uo">
                    <div className="flex justify-between items-center mb-2" data-id="element-1354" data-mp="13145bzgf7jrg">
                      <span className="text-gray-400" data-id="element-1355" data-mp="3ss0q05zphuo6">Page Visits</span>
                      <span className="text-white font-bold" data-id="element-1356" data-mp="3sp4tmm3ibe1u">370</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2 mb-2" data-id="element-1357" data-mp="3nur4h1ffu35m">
                      <div className="bg-orange-500 h-2 rounded-full" style={{
                      width: '79%'
                    }} data-id="element-1358" data-mp="3dqanu5u4wi0r"></div>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-1359" data-mp="3sdvrq1yu2l9q">
                      293 (79%) from Yelp Ads
                    </p>
                  </div>
                  <div className="bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-1360" data-mp="9bfdvdyvnb8">
                    <div className="flex justify-between items-center mb-2" data-id="element-1361" data-mp="56tdeqqg4i4g">
                      <span className="text-gray-400" data-id="element-1362" data-mp="a8sflxag98zt">Total Leads</span>
                      <span className="text-white font-bold" data-id="element-1363" data-mp="f7dkia0ncgul">138</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2 mb-2" data-id="element-1364" data-mp="2bzdrnja7fx9a">
                      <div className="bg-orange-500 h-2 rounded-full" style={{
                      width: '59%'
                    }} data-id="element-1365" data-mp="2gy73z8dr9i7v"></div>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-1366" data-mp="23zhe8aouhuvj">
                      81 (59%) from Yelp Ads
                    </p>
                  </div>
                </div>

                <div data-id="element-1367" data-mp="2dw2nj87wds56">
                  <h4 className="text-lg font-semibold text-white mb-4" data-id="element-1368" data-mp="2dvkf7d7ynngw">
                    Lead Mix
                  </h4>
                  <div className="bg-viridia-surface rounded-xl border border-gray-800 overflow-hidden" data-id="element-1369" data-mp="2ruk6ytqrsiha">
                    <table className="w-full text-left text-sm" data-id="element-1370" data-mp="2mx0jd386mtco">
                      <thead className="bg-gray-900 border-b border-gray-800" data-id="element-1371" data-mp="31l52g8b5d6vz">
                        <tr data-id="element-1372" data-mp="2wmk30b3pyr8l">
                          <th className="p-4 text-gray-400 font-medium" data-id="element-1373" data-mp="2woepvuyjwtfb">
                            Lead Type
                          </th>
                          <th className="p-4 text-gray-400 font-medium" data-id="element-1374" data-mp="36l9ki2rpe9ob">
                            Count
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800" data-id="element-1375" data-mp="3p7qvoq6nb94a">
                        <tr data-id="element-1376" data-mp="3u84tlfej92ol">
                          <td className="p-4 text-white flex items-center" data-id="element-1377" data-mp="3u6aguodf0gvl">
                            <PhoneIcon className="w-4 h-4 mr-2 text-orange-500" data-id="element-1378" data-mp="3g8lx5jv33fs4" />{' '}
                            Calls
                          </td>
                          <td className="p-4 text-white font-bold" data-id="element-1379" data-mp="36ik6qlf9dla1">52</td>
                        </tr>
                        <tr data-id="element-1380" data-mp="3ba28hbp3vpw2">
                          <td className="p-4 text-white flex items-center" data-id="element-1381" data-mp="3bbdbh9aq8tm4">
                            <MessageSquareIcon className="w-4 h-4 mr-2 text-orange-500" data-id="element-1382" data-mp="2xj6yfw2vf2qw" />{' '}
                            Messages
                          </td>
                          <td className="p-4 text-white font-bold" data-id="element-1383" data-mp="2nk7w0v8p83cc">50</td>
                        </tr>
                        <tr data-id="element-1384" data-mp="2sjb1ggslcbku">
                          <td className="p-4 text-white flex items-center" data-id="element-1385" data-mp="2sjkj9h3hfcfg">
                            <GlobeIcon className="w-4 h-4 mr-2 text-orange-500" data-id="element-1386" data-mp="3uwh0tb0ibalk" />{' '}
                            Website Visits
                          </td>
                          <td className="p-4 text-white font-bold" data-id="element-1387" data-mp="3qvx428jui8zy">33</td>
                        </tr>
                        <tr data-id="element-1388" data-mp="3m2kf1xb9806o">
                          <td className="p-4 text-white flex items-center" data-id="element-1389" data-mp="3h5c1ka11wkbr">
                            <MousePointerClickIcon className="w-4 h-4 mr-2 text-orange-500" data-id="element-1390" data-mp="3h37w3nni7d9d" />{' '}
                            CTA Clicks
                          </td>
                          <td className="p-4 text-white font-bold" data-id="element-1391" data-mp="376d1hfucpx0d">3</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Local Visibility */}
            <div className="p-8 lg:p-12 bg-viridia-surface" data-id="element-1392" data-mp="11efynkzzkhh4">
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-6" data-id="element-1393" data-mp="11btrhmh0c3zf">
                <MapPinIcon className="w-6 h-6 text-orange-500" data-id="element-1394" data-mp="13ym1fpcv61w3" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4" data-id="element-1395" data-mp="13wrs78ayfy7u">
                GEO & Local Painting Demand
              </h3>
              <ul className="space-y-4 mb-8" data-id="element-1396" data-mp="1dnvv39ncvsmb">
                <li className="flex items-start" data-id="element-1397" data-mp="1doxga6y35vhr">
                  <CheckCircleIcon className="w-5 h-5 text-orange-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-1398" data-mp="1io0cr3002te4" />
                  <span className="text-gray-300" data-id="element-1399" data-mp="l09so4pv6u11">
                    Finish Coat received more page views than similar businesses
                    on Yelp.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1400" data-mp="uxee22bvylgs">
                  <CheckCircleIcon className="w-5 h-5 text-orange-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-1401" data-mp="v00ny8m91d26" />
                  <span className="text-gray-300" data-id="element-1402" data-mp="yxeufsijp6qh">
                    Captured rising local search trends for 'Painters Interior',
                    'House Painters', and 'Interior Painting'.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.2} data-id="element-1403" data-mp="bbrbxqelmq3u">
          <div className="mt-12 text-center" data-id="element-1404" data-mp="6bmrjpaklb8c">
            <h3 className="text-2xl font-bold text-white mb-6" data-id="element-1405" data-mp="6ajypbtosyps">
              Want to see what's leaking leads in your pipeline?
            </h3>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-viridia-black bg-white rounded-lg hover:bg-gray-200 transition-all duration-300" data-id="element-1406" data-mp="fb05ov9n2nze">
              Book a Strategy Call
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1407" data-mp="1bqoptdlzpud" />
            </Link>
          </div>
        </ScrollReveal>
      </section>
    </div>;
}